import { computed, reactive, ref, shallowRef, watch } from 'vue'
import { createGhlClient, DEFAULT_API_VERSION } from '../api/ghl.js'
import {
  DEFAULT_SETTINGS,
  buildReport,
  normalizeContact,
  normalizeOpportunity,
} from '../lib/criteria.js'
import { guardarCache, leerCache, limpiarCache, rangoEnCache } from '../lib/cache.js'
import { DEFAULT_RANGE, cutoffDe, derivablesDe, rangoPorId } from '../lib/ranges.js'
import { demoData } from '../lib/demo.js'
import { readQueryConfig, onParentConfig } from '../lib/embed.js'

const STORAGE_KEY = 'ghl-nogestionados:settings'

const fmt = (n) => new Intl.NumberFormat('es-EC').format(n ?? 0)

export function useReport() {
  const query = readQueryConfig()

  // El token nunca vive aqui: lo pone el proxy desde el .env del servidor.
  // El modo 'direct' solo se activa explicitamente por query string.
  const config = reactive({
    locationId: query.locationId || import.meta.env.VITE_DEFAULT_LOCATION_ID || '',
    token: query.token || '',
    apiMode: query.apiMode === 'direct' ? 'direct' : 'proxy',
    version: DEFAULT_API_VERSION,
    embed: query.embed,
    demo: new URLSearchParams(location.search).get('demo') === '1',
    serverConfigured: false,
    accountName: '',
  })

  const settings = reactive(loadSettings(query))

  const range = ref(query.range && rangoPorId(query.range).id === query.range ? query.range : DEFAULT_RANGE)
  const loading = ref(false)
  const error = ref(null)
  const progress = ref('')
  const lastRun = ref(null)
  const fromCache = ref(false)
  const estimando = ref(false)
  const parcial = ref(null)
  let esperado = { contacts: 0, opportunities: 0 }
  const rangoCargado = ref(null)
  const estimacion = ref(null)
  const raw = shallowRef({ pipelines: [], contacts: [], opportunities: [] })

  const filters = reactive({
    pipelineId: query.pipeline || 'todos',
    reason: 'todos',
    severity: 'todas',
    owner: 'todos',
    search: '',
  })

  let abortController = null

  const cliente = () => createGhlClient({
    mode: config.apiMode,
    token: config.token,
    version: config.version,
  })

  const report = computed(() => buildReport({
    contacts: raw.value.contacts,
    opportunities: raw.value.opportunities,
    pipelines: raw.value.pipelines,
    settings,
  }))

  const visiblePipelines = computed(() => {
    const groups = report.value.pipelines
    return filters.pipelineId === 'todos'
      ? groups
      : groups.filter((g) => g.id === filters.pipelineId)
  })

  const filteredLeads = computed(() => {
    const q = filters.search.trim().toLowerCase()
    return report.value.leads.filter((l) => {
      if (filters.pipelineId !== 'todos' && l.pipelineId !== filters.pipelineId) return false
      if (filters.reason !== 'todos' && !l.reasons.some((r) => r.id === filters.reason)) return false
      if (filters.severity !== 'todas' && l.severity !== filters.severity) return false
      if (filters.owner === 'sin_asignar' && l.assignedTo) return false
      if (filters.owner !== 'todos' && filters.owner !== 'sin_asignar' && l.assignedTo !== filters.owner) return false
      if (q && !`${l.name} ${l.email || ''} ${l.phone || ''} ${l.stageName}`.toLowerCase().includes(q)) return false
      return true
    })
  })

  const owners = computed(() => {
    const set = new Set()
    for (const l of report.value.leads) if (l.assignedTo) set.add(l.assignedTo)
    return [...set].sort()
  })

  const ready = computed(() => config.demo || Boolean(config.locationId))

  /** Rangos que ya estan en cache: la UI los marca como instantaneos. */
  const rangosEnCache = computed(() => {
    if (config.demo) return derivablesDe('all')
    const r = rangoCargado.value || rangoEnCache(config.locationId)
    return r ? derivablesDe(r) : []
  })

  /**
   * Avisa cuando la ventana es mas corta que los umbrales: un lead estancado
   * 14 dias no puede aparecer si solo se cargaron los ultimos 7.
   */
  const avisoVentana = computed(() => {
    const dias = rangoPorId(range.value).days
    if (dias === null) return null
    const activos = []
    if (settings.enabled.ETAPA_ESTANCADA && settings.stagnantDays > dias) activos.push('estancado en etapa')
    if (settings.enabled.SIN_ACTIVIDAD && settings.noActivityDays > dias) activos.push('sin actividad')
    if (!activos.length) return null
    return `La ventana de ${dias} dias es mas corta que los umbrales de "${activos.join('" y "')}", `
      + 'asi que esos criterios apenas pueden dispararse. Amplia el rango para verlos.'
  })

  async function load({ force = false } = {}) {
    if (loading.value) return
    error.value = null
    estimacion.value = null

    if (config.demo) {
      const d = demoData()
      raw.value = {
        pipelines: d.pipelines,
        contacts: d.contacts.map(normalizeContact),
        opportunities: d.opportunities.map(normalizeOpportunity),
      }
      lastRun.value = new Date().toISOString()
      fromCache.value = false
      rangoCargado.value = 'all'
      return
    }

    if (!config.locationId) {
      error.value = 'Falta el locationId de la subcuenta (GHL_LOCATION_ID en el .env del servidor).'
      return
    }

    // 1. Cache: si ya hay una ventana igual o mas amplia, se recorta y listo.
    if (!force) {
      const cache = leerCache(config.locationId, range.value)
      if (cache) {
        raw.value = { pipelines: cache.pipelines, contacts: cache.contacts, opportunities: cache.opportunities }
        lastRun.value = new Date(cache.loadedAt).toISOString()
        rangoCargado.value = cache.rangeCargado
        fromCache.value = true
        progress.value = ''
        return
      }
    }

    // 2. Descarga incremental.
    //
    // Los lotes se van volcando a `raw` segun llegan, no al final: un barrido
    // historico son ~1.000 peticiones y dos minutos, y si se hiciera todo o
    // nada un fallo o una cancelacion a mitad tirarian todo lo descargado.
    loading.value = true
    fromCache.value = false
    parcial.value = null
    abortController?.abort()
    abortController = new AbortController()
    const signal = abortController.signal
    const since = cutoffDe(range.value)
    const client = cliente()

    const acumulado = { pipelines: [], contacts: [], opportunities: [] }
    esperado = { contacts: 0, opportunities: 0 }
    let pintados = 0


    // Repintar cuesta un buildReport completo, asi que se hace por tandas y no
    // en cada pagina de 100.
    const pintar = (forzar = false) => {
      const n = acumulado.contacts.length + acumulado.opportunities.length
      if (!forzar && n - pintados < 5000) return
      pintados = n
      raw.value = { ...acumulado }
    }

    let completo = false
    try {
      progress.value = 'Cargando pipelines...'
      acumulado.pipelines = await client.getPipelines(config.locationId, { signal })

      // El historico completo se trocea por meses: hay que saber donde empieza.
      let desdeSuelo
      if (since === null) {
        progress.value = 'Buscando el registro mas antiguo...'
        desdeSuelo = await client.primerRegistro(config.locationId, { signal })
      }

      progress.value = 'Cargando oportunidades...'
      const opps = await client.searchOpportunities(config.locationId, {
        since,
        desdeSuelo,
        signal,
        onBatch: (lote) => {
          for (const o of lote) acumulado.opportunities.push(normalizeOpportunity(o))
          pintar()
        },
        onProgress: (p) => {
          // Se guarda aqui y no solo al final: si la descarga se corta, el
          // aviso de resultado parcial necesita saber cuanto habia que traer.
          if (p.total) esperado.opportunities = p.total
          progress.value = `Oportunidades: ${fmt(p.loaded)}${p.total ? ' / ' + fmt(p.total) : ''}`
        },
      })

      progress.value = 'Cargando contactos...'
      const contactos = await client.searchContacts(config.locationId, {
        since,
        desdeSuelo,
        signal,
        onBatch: (lote) => {
          for (const c of lote) acumulado.contacts.push(normalizeContact(c))
          pintar()
        },
        onProgress: (p) => {
          if (p.total) esperado.contacts = p.total
          progress.value = `Contactos: ${fmt(p.loaded)}${p.total ? ' / ' + fmt(p.total) : ''}`
        },
      })

      completo = opps.completo && contactos.completo
    } catch (err) {
      if (err.name !== 'AbortError') error.value = err.message || String(err)
    } finally {
      pintar(true)
      progress.value = ''
      loading.value = false
    }

    lastRun.value = new Date().toISOString()

    // Solo se cachea y se da por cargado el rango si vino entero: guardar un
    // barrido a medias haria que al volver a este rango se sirvieran datos
    // incompletos como si fueran buenos.
    if (completo) {
      guardarCache(config.locationId, range.value, acumulado)
      rangoCargado.value = range.value
      parcial.value = null
    } else {
      rangoCargado.value = null
      parcial.value = {
        contacts: acumulado.contacts.length,
        opportunities: acumulado.opportunities.length,
        esperadoContacts: esperado.contacts,
        esperadoOpportunities: esperado.opportunities,
      }
    }
  }

  /**
   * Cambia de ventana temporal. Si los datos ya estan en cache el cambio es
   * inmediato; si toca descargar y la ventana es grande, primero se estima el
   * coste y se pregunta. Mientras no se confirme, el rango activo no cambia:
   * el informe en pantalla debe corresponder siempre a los datos cargados.
   */
  async function setRange(id, { confirmado = false } = {}) {
    if (loading.value || estimando.value) return
    if (id === range.value && (raw.value.contacts.length || raw.value.opportunities.length)) return

    if (config.demo || leerCache(config.locationId, id) || confirmado) {
      range.value = id
      return load()
    }

    if (config.locationId) {
      estimando.value = true
      progress.value = 'Calculando el volumen del rango...'
      abortController?.abort()
      abortController = new AbortController()
      try {
        const conteo = await cliente().contarRango(config.locationId, cutoffDe(id), {
          signal: abortController.signal,
        })
        if (conteo.peticiones > 25) {
          estimacion.value = { ...conteo, range: id }
          return
        }
      } catch (err) {
        if (err.name === 'AbortError') return
        // Si la estimacion falla se intenta la carga igualmente.
      } finally {
        estimando.value = false
        progress.value = ''
      }
    }

    range.value = id
    return load()
  }

  function confirmarCarga() {
    const id = estimacion.value?.range
    estimacion.value = null
    if (!id) return
    range.value = id
    return load({ force: true })
  }

  function cancelarCarga() {
    estimacion.value = null
    estimando.value = false
    abortController?.abort()
    loading.value = false
    progress.value = ''
  }

  function refrescar() {
    limpiarCache(config.locationId)
    rangoCargado.value = null
    return load({ force: true })
  }

  /**
   * Arranque: el locationId y la version salen del .env a traves de /api/config,
   * de modo que la pagina no pide ninguna credencial.
   */
  async function bootstrap() {
    if (!config.demo && config.apiMode === 'proxy') {
      try {
        const res = await fetch('/api/config')
        const tipo = res.headers.get('content-type') || ''

        // Si /api/config devuelve el index.html es que la pagina se esta
        // sirviendo como sitio estatico y server/proxy.js no esta corriendo.
        // Sin el no hay configuracion ni acceso al API, y el sintoma sin este
        // aviso seria un enganoso "falta la subcuenta".
        if (!tipo.includes('json')) {
          error.value = 'Esta pagina se esta sirviendo como sitio estatico: /api/config ha '
            + `devuelto ${tipo.split(';')[0] || 'HTTP ' + res.status} en vez de JSON. `
            + 'Falta arrancar el servidor Node (node server/proxy.js), que es quien sirve '
            + 'la configuracion y firma las llamadas a GoHighLevel. Publicar solo la carpeta '
            + 'dist no basta.'
          return
        }

        const data = await res.json()
        if (data.defaultLocationId) config.locationId = data.defaultLocationId
        if (data.apiVersion) config.version = data.apiVersion
        config.serverConfigured = Boolean(data.hasServerToken && data.defaultLocationId)

        if (!data.hasServerToken) {
          error.value = 'El servidor esta corriendo pero no tiene GHL_TOKEN. Definelo en el '
            + 'entorno (o en el .env) y reinicia.'
          return
        }
        if (!data.defaultLocationId) {
          error.value = 'El servidor esta corriendo pero no tiene GHL_LOCATION_ID. Definelo en '
            + 'el entorno (o en el .env) y reinicia.'
          return
        }
      } catch (err) {
        error.value = `No se pudo leer /api/config (${err.message}). Comprueba que el servidor `
          + 'Node esta arrancado y accesible.'
        return
      }
    }
    // El nombre de la subcuenta se pide al API en vez de escribirlo a mano.
    if (config.locationId && !config.demo) {
      cliente().getLocation(config.locationId)
        .then((loc) => { if (loc?.name) config.accountName = loc.name })
        .catch(() => { /* sin scope locations.readonly: se muestra el id */ })
    }

    if (ready.value) await load()
  }

  onParentConfig((data) => {
    let changed = false
    for (const key of ['locationId', 'token', 'apiMode']) {
      if (data[key] && data[key] !== config[key]) { config[key] = data[key]; changed = true }
    }
    if (data.range) range.value = data.range
    if (data.settings) Object.assign(settings, data.settings)
    if (changed) load()
  })

  watch(settings, (val) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(val)) } catch { /* modo privado */ }
  }, { deep: true })

  function resetSettings() {
    Object.assign(settings, structuredClone(DEFAULT_SETTINGS))
  }

  return {
    config, settings, filters, range,
    loading, estimando, error, progress, lastRun, ready, fromCache, rangoCargado, rangosEnCache,
    estimacion, avisoVentana, parcial,
    raw, report, visiblePipelines, filteredLeads, owners,
    load, bootstrap, setRange, confirmarCarga, cancelarCarga, refrescar, resetSettings,
  }
}

function loadSettings(query) {
  const base = structuredClone(DEFAULT_SETTINGS)
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')
    if (saved) Object.assign(base, saved, { enabled: { ...base.enabled, ...(saved.enabled || {}) } })
  } catch { /* ignorar storage corrupto */ }
  if (query.stagnantDays) base.stagnantDays = query.stagnantDays
  if (query.noActivityDays) base.noActivityDays = query.noActivityDays
  return base
}
